import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { sharePublicView, wsChannel } from '@xdocs/schemas';
import type { DiscoverGetResult, ErrorAlert } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { AuthTransportError, requestAuthOp } from '../../auth/authTransport.js';
import { resolveWsUrl } from '../../lib/env.js';
import { useWs } from '../../ws/WsProvider.js';

export interface UseDiscoverShareResult {
  view: DiscoverGetResult | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
}

/**
 * `discover.get` (F62) — the public read view for `/discover/:shareId`.
 * Gated server-side (`visibility='public' AND moderationStatus='approved'`,
 * else `NOT_FOUND` — existence-leak defense, §15). Kept live via
 * `share:{shareId}`'s `moderation_verdict`/`takedown`/`revoked` events.
 *
 * Deliberately WORKS while logged out (F62/F63 AC "no account needed"):
 * `WsClient` only ever connects with an access token (`connect()` runs
 * `auth.handshake` first), so an anonymous visitor never reaches
 * `'connected'`. For that case this falls back to the same one-shot
 * pre-handshake raw-socket exchange the `auth.*` ops use
 * (`authTransport.requestAuthOp`) — `discover.get` is on the server's
 * `PUBLIC_PREAUTH_READ_OPS` allowlist (`ws/dispatch.ts`), gated
 * server-side on `visibility='public' AND moderationStatus='approved'`.
 * The one-shot result is parsed through `sharePublicView` at this trust
 * boundary (coerces timestamps — the raw payload's `Date`s are ISO strings).
 */
export function useDiscoverShare(shareId: string): UseDiscoverShareResult {
  const { wsClient, connectionState } = useWs();
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const anonymous = status === 'unauthenticated';
  const enabled = shareId.length > 0 && (connectionState === 'connected' || anonymous);

  const query = useQuery<DiscoverGetResult, ErrorAlert>({
    queryKey: ['share', shareId, 'discover-get', anonymous ? 'anon' : 'authed'],
    queryFn: async () => {
      if (connectionState === 'connected') {
        return requestTyped(wsClient, 'discover.get', { shareId });
      }
      try {
        const raw = await requestAuthOp<unknown>(resolveWsUrl(), 'discover.get', { shareId });
        return sharePublicView.parse(raw);
      } catch (err) {
        // Normalize to the ErrorAlert shape the page's error branches key on
        // (`error?.code === 'NOT_FOUND'` → the "isn't public" state): a real
        // Error instance (lint: only-throw-error) carrying the alert's
        // `code`/`message`/`fields`, structurally an ErrorAlert.
        if (err instanceof AuthTransportError) throw Object.assign(new Error(err.alert.message), err.alert);
        throw err;
      }
    },
    enabled,
    retry: false,
  });

  useEffect(() => {
    const channel = wsChannel.share(shareId);
    return wsClient.subscribe(channel, () => {
      void queryClient.invalidateQueries({ queryKey: ['share', shareId, 'discover-get'] });
    });
  }, [wsClient, queryClient, shareId]);

  return {
    view: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.isError ? query.error : null,
    refetch: () => void query.refetch(),
  };
}
